import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Upload } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { DashboardWelcomeBanner } from './DashboardWelcomeBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard-banners`;
const EMPTY = { name: '', audience: 'everyone', greeting: '', tagline: '', message: '', start_date: '', end_date: '', accent_color: '#922A8C', repeat_yearly: false };
const HEX = /^#[0-9a-fA-F]{6}$/;
const BANNER_W = 2000;
const BANNER_H = 453;

// Center-crops any image to the built-in header shape (2000 x 453), scaling evenly with no stretching.
const cropToBanner = (f) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(f);
  const img = new Image();
  img.onload = () => {
    const scale = Math.max(BANNER_W / img.naturalWidth, BANNER_H / img.naturalHeight);
    const sw = BANNER_W / scale;
    const sh = BANNER_H / scale;
    const canvas = document.createElement('canvas');
    canvas.width = BANNER_W;
    canvas.height = BANNER_H;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, (img.naturalWidth - sw) / 2, (img.naturalHeight - sh) / 2, sw, sh, 0, 0, BANNER_W, BANNER_H);
    URL.revokeObjectURL(url);
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error('crop failed'));
      const name = `${f.name.replace(/\.[^.]+$/, '')}.webp`;
      resolve({ file: new File([blob], name, { type: 'image/webp' }), small: img.naturalWidth < BANNER_W || img.naturalHeight < BANNER_H });
    }, 'image/webp', 0.92);
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('unreadable')); };
  img.src = url;
});
export const AUDIENCE_LABELS = { leadership: 'Leadership (System Admin, Executive, Operations Manager)', staff: 'All other staff', everyone: 'Everyone' };

export const BannerFormDialog = ({ open, onOpenChange, banner, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [small, setSmall] = useState(false);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isDefault = !!banner?.is_default;

  useEffect(() => {
    if (!open) return;
    setForm(banner ? { name: banner.name, audience: banner.audience, greeting: banner.greeting, tagline: banner.tagline || '', message: banner.message || '', start_date: banner.start_date || '', end_date: banner.end_date || '', accent_color: banner.accent_color || '#922A8C', repeat_yearly: !!banner.repeat_yearly } : EMPTY);
    setFile(null);
    setSmall(false);
  }, [open, banner]);
  useEffect(() => {
    if (!file) { setPreview(null); return undefined; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) return toast.error('Upload a JPG, PNG or WebP image');
    if (f.size > 5 * 1024 * 1024) return toast.error('Images must be 5 MB or smaller');
    try {
      const cropped = await cropToBanner(f);
      setFile(cropped.file);
      setSmall(cropped.small);
    } catch {
      toast.error('This image could not be read. Try another JPG, PNG or WebP image.');
    }
  };

  const save = async () => {
    if (!form.name.trim() || !form.greeting.trim()) return toast.error('Add a banner name and a greeting');
    if (!banner && !file) return toast.error('Choose a banner image');
    if (!HEX.test(form.accent_color)) return toast.error('Pick an accent color, like #922A8C');
    if (!!form.start_date !== !!form.end_date) return toast.error('Pick both a start date and an end date, or leave both empty');
    if (form.start_date && form.end_date < form.start_date) return toast.error('The end date must be on or after the start date');
    setSaving(true);
    try {
      if (banner) {
        if (file) { const fd = new FormData(); fd.append('file', file); await axios.put(`${API}/${banner.id}/image`, fd); }
        await axios.put(`${API}/${banner.id}`, form);
      } else {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        fd.append('file', file);
        await axios.post(API, fd);
      }
      toast.success(banner ? 'Banner saved' : form.start_date ? 'Banner created. It will show automatically on the dates you picked.' : 'Banner created. Switch it on when you are ready.');
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the banner');
    } finally { setSaving(false); }
  };

  const previewBanner = { ...form, is_default: isDefault, default_kind: banner?.default_kind, accent_color: HEX.test(form.accent_color) ? form.accent_color : '#922A8C', greeting: form.greeting || 'Your greeting', image_url: preview || banner?.image_url, name: form.name };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="banner-form-dialog">
        <DialogHeader>
          <DialogTitle>{isDefault ? 'Edit Default Banner' : banner ? 'Edit Banner' : 'New Seasonal Banner'}</DialogTitle>
          <DialogDescription>Use a wide image (about 2000 × 450 pixels) with plain space on the left for the text.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Banner name</Label><Input value={form.name} onChange={set('name')} maxLength={120} placeholder="Holiday Season 2026" data-testid="banner-name-input" /></div>
          {isDefault ? (
            <div className="space-y-1.5"><Label>Who sees it</Label><p className="text-sm min-h-10 flex items-center text-muted-foreground" data-testid="banner-default-audience">{AUDIENCE_LABELS[form.audience]}, whenever no seasonal banner is on</p></div>
          ) : (
            <div className="space-y-1.5"><Label>Who sees it</Label>
              <Select value={form.audience} onValueChange={(v) => setForm((f) => ({ ...f, audience: v }))}>
                <SelectTrigger data-testid="banner-audience-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(AUDIENCE_LABELS).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`banner-audience-${k}`}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5 sm:col-span-2"><Label>Welcome back line <span className="text-muted-foreground font-normal">(the first name is added automatically)</span></Label>
            <div className="flex items-center gap-2"><Input value={form.greeting} onChange={set('greeting')} maxLength={60} placeholder="Happy Holidays" data-testid="banner-greeting-input" /><span className="text-sm text-muted-foreground whitespace-nowrap">, Jane!</span></div>
          </div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Accent line</Label><Input value={form.tagline} onChange={set('tagline')} maxLength={120} placeholder="Thank you for all you do this year." data-testid="banner-tagline-input" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Accent color <span className="text-muted-foreground font-normal">(used for the accent line)</span></Label>
            <div className="flex items-center gap-2">
              <input type="color" value={HEX.test(form.accent_color) ? form.accent_color : '#922A8C'} onChange={set('accent_color')} className="h-10 w-14 rounded-md border cursor-pointer bg-transparent p-1" aria-label="Accent color" data-testid="banner-accent-color-picker" />
              <Input value={form.accent_color} onChange={set('accent_color')} maxLength={7} className="w-32 font-mono" data-testid="banner-accent-color-input" />
              {form.accent_color.toUpperCase() !== '#922A8C' && <Button type="button" variant="ghost" size="sm" onClick={() => setForm((f) => ({ ...f, accent_color: '#922A8C' }))} data-testid="banner-accent-reset-btn">Use MyHELP purple</Button>}
            </div>
          </div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Grey line</Label><Textarea rows={2} value={form.message} onChange={set('message')} maxLength={300} placeholder="Wishing you and your family a restful season." data-testid="banner-message-input" /></div>
          {!isDefault && <div className="space-y-1.5 sm:col-span-2">
            <Label>Schedule <span className="text-muted-foreground font-normal">(optional — shows all day on these dates, Central time, and switches itself on and off)</span></Label>
            <div className="flex flex-wrap items-center gap-2">
              <Input type="date" className="w-44" value={form.start_date} onChange={set('start_date')} aria-label="Start date" data-testid="banner-start-date-input" />
              <span className="text-sm text-muted-foreground">to</span>
              <Input type="date" className="w-44" value={form.end_date} min={form.start_date || undefined} onChange={set('end_date')} aria-label="End date" data-testid="banner-end-date-input" />
              {(form.start_date || form.end_date) && <Button type="button" variant="ghost" size="sm" onClick={() => setForm((f) => ({ ...f, start_date: '', end_date: '', repeat_yearly: false }))} data-testid="banner-clear-dates-btn">Clear dates</Button>}
            </div>
            {form.start_date && form.end_date && (
              <label className="flex items-center gap-2 text-sm cursor-pointer w-fit" data-testid="banner-repeat-yearly">
                <Checkbox checked={form.repeat_yearly} onCheckedChange={(v) => setForm((f) => ({ ...f, repeat_yearly: !!v }))} data-testid="banner-repeat-yearly-checkbox" />
                Repeat every year <span className="text-xs text-muted-foreground">(shows again on these dates each year)</span>
              </label>
            )}
            {!form.start_date && <p className="text-xs text-muted-foreground">No dates: use the Show switch to turn it on and off yourself.</p>}
          </div>}
          <div className="space-y-1.5 sm:col-span-2"><Label>Banner image</Label>
            <label className="flex items-center gap-2 h-10 px-3 rounded-md border border-dashed cursor-pointer text-sm hover:bg-muted/50 transition-colors">
              <Upload className="h-4 w-4" />{file ? file.name : banner ? 'Replace image (optional)' : 'Choose an image (JPG, PNG or WebP, up to 5 MB)'}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid="banner-image-input" />
            </label>
            {file && <p className="text-xs text-muted-foreground" data-testid="banner-crop-note">Cropped from the center to fit the header (2000 × 453). The preview below shows exactly how it will look.</p>}
            {file && small && <p className="text-xs text-amber-700" data-testid="banner-small-warning">This image is smaller than the header (2000 × 453 pixels), so it was enlarged to fill it and may look soft. A larger image will look sharper.</p>}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Preview</Label>
          <DashboardWelcomeBanner firstName="Jane" banner={previewBanner} testId="banner-preview" preview />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="banner-save-btn">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save Banner</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
